import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  canLoad as canLoadHelper,
  COOKIE_CONSENT_VERSION,
  createDefaultConsent,
  readStoredCookieConsent,
  shouldShowConsentBanner,
  writeStoredCookieConsent,
} from "./cookieConsent";

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consentRecord, setConsentRecord] = useState(() => ({
    version: COOKIE_CONSENT_VERSION,
    updatedAt: null,
    consent: createDefaultConsent(),
  }));

  const [shouldShowBanner, setShouldShowBanner] = useState(false);
  const [isCookieSettingsOpen, setIsCookieSettingsOpen] = useState(false);

  useEffect(() => {
    const stored = readStoredCookieConsent();

    if (shouldShowConsentBanner(stored)) {
      // Version changed or missing => be conservative: essential-only until user opts in again.
      setConsentRecord({
        version: COOKIE_CONSENT_VERSION,
        updatedAt: null,
        consent: createDefaultConsent(),
      });
      setShouldShowBanner(true);
      return;
    }

    // This project does not use analytics/marketing cookies; keep the stored record aligned.
    const sanitizedConsent = {
      ...stored?.consent,
      necessary: true,
      analytics: false,
      marketing: false,
    };

    if (stored?.consent?.analytics || stored?.consent?.marketing) {
      const record = writeStoredCookieConsent(sanitizedConsent);
      setConsentRecord(record);
    } else {
      setConsentRecord({
        ...stored,
        consent: sanitizedConsent,
      });
    }
    setShouldShowBanner(false);
  }, []);

  const saveConsent = useCallback((nextConsent) => {
    const record = writeStoredCookieConsent(nextConsent);
    setConsentRecord(record);
    setShouldShowBanner(false);
    setIsCookieSettingsOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({
      necessary: true,
      preferences: true,
      analytics: false,
      marketing: false,
    });
  }, [saveConsent]);

  const rejectNonEssential = useCallback(() => {
    saveConsent({
      necessary: true,
      preferences: false,
      analytics: false,
      marketing: false,
    });
  }, [saveConsent]);

  const openCookieSettings = useCallback(() => {
    setIsCookieSettingsOpen(true);
  }, []);

  const closeCookieSettings = useCallback(() => {
    setIsCookieSettingsOpen(false);
  }, []);

  const canLoad = useCallback(
    (category) => canLoadHelper(consentRecord?.consent, category),
    [consentRecord?.consent]
  );

  const value = useMemo(
    () => ({
      consent: consentRecord.consent,
      consentRecord,
      version: consentRecord.version,
      updatedAt: consentRecord.updatedAt,
      shouldShowBanner,
      isCookieSettingsOpen,
      saveConsent,
      acceptAll,
      rejectNonEssential,
      openCookieSettings,
      closeCookieSettings,
      canLoad,
    }),
    [
      acceptAll,
      canLoad,
      closeCookieSettings,
      consentRecord,
      isCookieSettingsOpen,
      openCookieSettings,
      rejectNonEssential,
      saveConsent,
      shouldShowBanner,
    ]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error("useCookieConsent must be used within CookieConsentProvider");
  }
  return ctx;
}
